/**
 * What a federal-tax filing costs the customer, in USD cents — the one table
 * the customer app's /federal-tax landing (late-filing years, checkout
 * analytics) and the API's dashboard deadline card both read, so the card
 * never quotes a price the landing does not sell.
 *
 * `standard` is the on-time filing for last tax year; `late` is any year whose
 * due date has passed without a filing; `amended` corrects a return already
 * filed (often by another provider) and costs exactly the late price (owner
 * decision 2026-10-16: "this is like late filing fee, the same for us"). The
 * Stripe prices that charge these amounts live in
 * `apps/api/src/federal-tax/eligibility.ts` (amended reuses the late prices).
 */

export type FederalTaxPriceScenario = "smllc" | "mmllc" | "ccorp";

export const FEDERAL_TAX_FILING_MODES = [
  "standard",
  "late",
  "amended",
] as const;
export type FederalTaxFilingMode = (typeof FEDERAL_TAX_FILING_MODES)[number];

export function isFederalTaxFilingMode(
  value: unknown,
): value is FederalTaxFilingMode {
  return (
    typeof value === "string" &&
    (FEDERAL_TAX_FILING_MODES as readonly string[]).includes(value)
  );
}

const LATE_PRICE_CENTS = { smllc: 75000, mmllc: 150000, ccorp: 150000 };

export const FEDERAL_TAX_PRICE_CENTS: Readonly<
  Record<
    FederalTaxFilingMode,
    Readonly<Record<FederalTaxPriceScenario, number>>
  >
> = {
  standard: { smllc: 39900, mmllc: 89900, ccorp: 89900 },
  late: LATE_PRICE_CENTS,
  amended: LATE_PRICE_CENTS,
};

export function federalTaxPriceCents(
  scenario: FederalTaxPriceScenario,
  mode: FederalTaxFilingMode,
): number {
  return FEDERAL_TAX_PRICE_CENTS[mode][scenario];
}

/**
 * What a customer still owes when they paid `paidCents` (0 for a Full Suite
 * filing, which is included) on the normal-fee extension path and the Form 7004
 * proof was then rejected: the late fee less what they paid, never negative.
 */
export function federalTaxLateFeeGapCents(
  scenario: FederalTaxPriceScenario,
  paidCents: number,
): number {
  const paid = Number.isFinite(paidCents) && paidCents > 0 ? paidCents : 0;
  return Math.max(0, FEDERAL_TAX_PRICE_CENTS.late[scenario] - paid);
}
